import { VALID_CATEGORIES, VALID_COUNTRIES, VALID_IMPACTS } from './utils';

import type { CalendarFilter } from './types';
import type { FinancialCalendarDailyCount, FinancialEventImpact } from '@/entities/financial-calendar';

/**
 * TRAD-3709 P1 — 금융캘린더 «일자별» 페이지(`/financial-calendar/YYYY-MM-DD`) 순수 로직.
 * 검색 노출용 고유 URL: 그날의 경제지표 발표·실적·공시(발표값 포함)를 기존 목록 컴포넌트로 보인다.
 */

const DAY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** `YYYY-MM-DD` 이고 달력에 실제로 있는 날짜인가(2026-02-30 같은 값 거절). */
export const isValidCalendarDay = (value: string | undefined | null): value is string => {
  if (!value) return false;
  const m = DAY_RE.exec(value);
  if (!m) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const date = new Date(Date.UTC(y, mo - 1, d));
  return date.getUTCFullYear() === y && date.getUTCMonth() === mo - 1 && date.getUTCDate() === d;
};

/** 날짜만 더하고 뺀다(UTC 산술 — 시간대·서머타임 무관). */
export const shiftCalendarDay = (day: string, deltaDays: number): string => {
  const [y, m, d] = day.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d + deltaDays));
  return date.toISOString().slice(0, 10);
};

/**
 * 그날 하루의 목록 필터. 일자 페이지는 «그날 있었던 일 전부»가 목적이라 중요도·카테고리·국가를 모두 연다
 * (메인 캘린더의 기본 노출 정책 DEFAULT_IMPACTS 와 다르다 — 검색으로 들어온 사람에게 «주요만»을 기본으로 숨기지 않는다).
 */
export const calendarDayFilter = (day: string): CalendarFilter => ({
  from: day,
  to: day,
  categories: [...VALID_CATEGORIES],
  countries: [...VALID_COUNTRIES],
  impacts: [...VALID_IMPACTS],
  view: 'list',
});

export const calendarDayPath = (day: string): string => `/financial-calendar/${day}`;

/**
 * TRAD-3725 — 일자 페이지의 «필터 없음» 중요도 기준 = calendarDayFilter 가 여는 «전체».
 * EventList 의 빈 상태 판정(hasActiveCalendarFilter)에 넘긴다. 일자 페이지엔 필터 UI 가 없으므로 이 기준과 다를 일이 없다.
 */
export const CALENDAR_DAY_PRISTINE_IMPACTS: FinancialEventImpact[] = [...VALID_IMPACTS];

/**
 * TRAD-3726 — 일자 페이지는 «그날 이벤트가 1건 이상»일 때만 색인한다(팀장 설계 결정 B, 2026-10-13).
 * 종전엔 형식만 맞으면 1999-01-01 도 index,follow + 자기 canonical → 이벤트 0건 빈 페이지가 무한 URL 로 색인 후보였다.
 *
 * counts = daily-counts(from=to=그날) 응답. 서버는 0건 날짜를 «0 행» 이 아니라 «행 없음»으로 준다
 * (dev 실측 2026-10-15: 1999-01-01·2026-10-18 → result [] / 2026-10-14 → [{date, totalCount: 2, …}]).
 * 🔴 모르면 색인하지 않는다 — 로딩 중·실패(undefined)도 false(보수적, 결정 B 에 적힌 단점 그대로). 프리렌더는
 *    업스트림이 조용해질 때까지 기다린 뒤 캡처하므로 정상 빌드에선 «로딩 중 noindex» 가 산출물에 남지 않는다.
 */
export const isCalendarDayIndexable = (
  counts: readonly FinancialCalendarDailyCount[] | undefined,
  day: string,
): boolean => (counts ?? []).some(count => count.date === day && count.totalCount > 0);
